import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthStyle from '../Styles/Auth.module.css';
import HomeDashboardStyle from '../Styles/HomeDashboard.module.css';
import logo from '../assets/GMF logo no background.png';
import { verifyToken } from '../Functions/verifyToken';
import { useAuth } from '../Components/AuthContext';

const Home = () => {
    const navigate = useNavigate();
    const url = process.env.REACT_APP_API_URL;

    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [loading, setLoading] = useState(true);
    const [unreadChats, setUnreadChats] = useState([]);
    const [unansweredChats, setUnansweredChats] = useState([]);
    const [accountsToApprove, setAccountsToApprove] = useState(0); // Total count
    const [healthcareWorkers, setHealthcareWorkers] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const { token, user, role } = useAuth();

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                if (!token) return;

                const headers = { Authorization: `Bearer ${token}` };

                if (role === 'GMF_EMPLOYEE') {
                    // unread chats in conversations you're already in
                    const unreadResponse = await fetch(`${url}/chat/hcw-unread-messages`, { headers });
                    setUnreadChats(await unreadResponse.json());

                    // chat conversations no one has responded to yet
                    const unansweredResponse = await fetch(`${url}/chat/list`, { headers });
                    setUnansweredChats(await unansweredResponse.json());

                    // number of unverified accounts of each type waiting for approval/denial
                    const unverifiedInstitutionsResponse = await fetch(`${url}/institution-account/list?verified=false`, { headers });
                    const unverifiedHCWsResponse = await fetch(`${url}/healthcare-worker/list?verified=false`, { headers });
                    const unverifiedGMFResponse = await fetch(`${url}/gmf-employee/list?verified=false`, { headers });

                    const unverifiedInstitutions = await unverifiedInstitutionsResponse.json();
                    const unverifiedHCWs = await unverifiedHCWsResponse.json();
                    const unverifiedGMF = await unverifiedGMFResponse.json();

                    setAccountsToApprove(unverifiedInstitutions.length + unverifiedHCWs.length + unverifiedGMF.length);

                    // total number of institutions
                    const institutionsResponse = await fetch(`${url}/institution-account/list`, { headers });
                    setInstitutions(await institutionsResponse.json());

                    // total number of HCWs
                    const hcwsResponse = await fetch(`${url}/healthcare-worker/list`, { headers });
                    setHealthcareWorkers(await hcwsResponse.json());
                } else if (role === 'INSTITUTION') {
                    const hcwsResponse = await fetch(`${url}/healthcare-worker/list`, { headers });
                    setHealthcareWorkers(await hcwsResponse.json());
                }
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        verifyToken(token).then((valid) => {
            setIsLoggedIn(valid);
            if (valid) fetchDashboardData();
            else setLoading(false);
        });
    }, [token, role]);

    if (loading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
                <h1>Loading the homepage... ⏳</h1>
            </div>
        );
    }

    if (!isLoggedIn) {
        return (
			<div className={AuthStyle.home}>
				<header className={AuthStyle.headerSection}>
					<div className={AuthStyle.contentContainer}>
						<img
							src={logo}
							alt="MD-Sensei Logo"
							className={AuthStyle.logo}
						/>
						<h1 className={AuthStyle.title}>
							Welcome to MD-Sensei!
						</h1>
						<p>
							MD-Sensei is your AI-powered Digital Clinical
							Mentor, designed to support healthcare workers in
							resource-limited settings. With secure cloud-based
							servers and offline functionality, MD-Sensei ensures
							uninterrupted access to critical tools. Healthcare
							workers can interact with an AI chatbot even when
							offline, ask follow-up questions to medical
							professionals, and access Q&A support. Key features
							like a secure server in the cloud and seamless data
							synchronization to the app empower HCWs to improve
							patient outcomes, even in the most challenging
							environments.
						</p>
						<button
							className={AuthStyle.submitButton}
							onClick={() => navigate("/login")}
						>
							Login
						</button>
                        {/* same styling as the login button, but green and with a different color */}
                        <button
                            className={AuthStyle.alternateSubmitButton}
                            onClick={() => navigate("/create-account")}
                        >
                            Create Account
                        </button>
                        {/* same styling as others */}
                        <button
                            className={AuthStyle.submitButton}
                            style={{ backgroundColor: 'var(--hover-alternate-button-color)' }}
                            onClick={() => navigate("/app")}
                        >
                            Get Mobile App for in the Field
                        </button>
					</div>
				</header>
			</div>
		);
    }

    return (
        <div className={HomeDashboardStyle.dashboard}>
            <header className={`${HomeDashboardStyle.header} ${HomeDashboardStyle.stickyHeader}`}>
                <h1>
                    Hi, {user?.first_name || user?.institution_name || 'User'}! 😊
                </h1>
                <p>
                    Here's an overview of the system, updates, and actions you can take to stay on top of things. 🚀
                </p>
            </header>
            <div className={HomeDashboardStyle.grid}>
                {role === 'GMF_EMPLOYEE' && (
                    <>
                        <div className={HomeDashboardStyle.card}>
                            <h2>Chats with Unread Messages</h2>
                            {unreadChats.length > 0 ? (
                                <p>You have <strong>{unreadChats.length}</strong> unread messages in ongoing conversations.</p>
                            ) : (
                                <p>🎉 No unread messages! You're all caught up.</p>
                            )}
                            <button
                                className={HomeDashboardStyle.actionButton}
                                onClick={() => navigate('/chat')}
                            >
                                View Chats
                            </button>
                        </div>
                        <div className={HomeDashboardStyle.card}>
                            <h2>New Conversations</h2>
                            {unansweredChats.length > 0 ? (
                                <p>There are <strong>{unansweredChats.length}</strong> new conversations which haven't been answered yet.</p>
                            ) : (
                                <p>🎉 No new conversations! Great job staying on top of things.</p>
                            )}
                            <button
                                className={HomeDashboardStyle.actionButton}
                                onClick={() => navigate('/chat')}
                            >
                                Respond to Chats
                            </button>
                        </div>
                        <div className={HomeDashboardStyle.card}>
                            <h2>Accounts to Approve</h2>
                            {accountsToApprove > 0 ? (
                                <p>You have <strong>{accountsToApprove}</strong> accounts pending approval.</p>
                            ) : (
                                <p>🎉 No accounts to approve! Everything is up to date.</p>
                            )}
                            <button
                                className={HomeDashboardStyle.actionButton}
                                onClick={() => navigate('/approve-requests')}
                            >
                                Approve Accounts
                            </button>
                        </div>
                        <div className={HomeDashboardStyle.card}>
                            <h2>Institutions</h2>
                            <p>There are <strong>{institutions.length}</strong> institutions in the system.</p>
                            <button
                                className={HomeDashboardStyle.actionButton}
                                onClick={() => navigate('/view-institutions')}
                            >
                                View Institutions
                            </button>
                        </div>
                        <div className={HomeDashboardStyle.card}>
                            <h2>Healthcare Workers</h2>
                            <p>There are <strong>{healthcareWorkers.length}</strong> healthcare workers in the system.</p>
                            <button
                                className={HomeDashboardStyle.actionButton}
                                onClick={() => navigate('/view-healthcare-workers')}
                            >
                                View Healthcare Workers
                            </button>
                        </div>
                    </>
                )}
                {role === 'INSTITUTION' && (
                    <>
                        <div className={HomeDashboardStyle.card}>
                            <h2>Healthcare Workers</h2>
                            <p>You have <strong>{healthcareWorkers.length}</strong> healthcare workers associated with your institution.</p>
                            <button
                                className={HomeDashboardStyle.actionButton}
                                onClick={() => navigate('/view-healthcare-workers')}
                            >
                                Manage Healthcare Workers
                            </button>
                        </div>
                        <div className={HomeDashboardStyle.card}>
                            <h2>AI Conversations</h2>
                            <p>View and manage AI conversations for your healthcare workers.</p>
                            <button
                                className={HomeDashboardStyle.actionButton}
                                onClick={() => navigate('/hcw-ai-chats')}
                            >
                                View Conversations
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Home;